import { createBrowserRouter } from 'react-router-dom';
import { ROUTES } from '@/shared/config/routes';
import { RequireAuth, RequireRole } from '@/features/auth';
import { RootLayout } from './layouts/RootLayout';
import { AdminLayout } from './layouts/AdminLayout';
import { HomePage } from '@/pages/HomePage';
import { AboutPage } from '@/pages/AboutPage';
import { ArticlePage } from '@/pages/ArticlePage';
import { AuthPage } from '@/pages/AuthPage';
import { ProfilePage } from '@/pages/ProfilePage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { RouteErrorBoundary } from './RouteErrorBoundary';
import { lazyPage } from './lazyPage';

// Staff-only and email-landing surfaces are code-split: public readers never
// download the editor or admin panel. Public reading paths stay eager so the
// first paint needs no extra round-trip.
const EditorPage = lazyPage(() => import('@/pages/EditorPage'), 'EditorPage');
const AdminDashboardPage = lazyPage(
  () => import('@/pages/admin/AdminDashboardPage'),
  'AdminDashboardPage',
);
const AdminArticlesPage = lazyPage(
  () => import('@/pages/admin/AdminArticlesPage'),
  'AdminArticlesPage',
);
const AdminWritersPage = lazyPage(() => import('@/pages/admin/AdminWritersPage'), 'AdminWritersPage');
const AdminSubscribersPage = lazyPage(
  () => import('@/pages/admin/AdminSubscribersPage'),
  'AdminSubscribersPage',
);
const AdminSettingsPage = lazyPage(
  () => import('@/pages/admin/AdminSettingsPage'),
  'AdminSettingsPage',
);
const SubscribeConfirmPage = lazyPage(
  () => import('@/pages/SubscribeConfirmPage'),
  'SubscribeConfirmPage',
);
const UnsubscribePage = lazyPage(() => import('@/pages/UnsubscribePage'), 'UnsubscribePage');
const InviteAcceptPage = lazyPage(() => import('@/pages/InviteAcceptPage'), 'InviteAcceptPage');

/**
 * Route table. Public routes render inside RootLayout; the editor and admin get
 * their own full-screen shells behind role guards. Every branch carries the
 * error boundary so a failed render or chunk fetch shows a recoverable page.
 */
export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, element: <HomePage /> },
      { path: 'about', element: <AboutPage /> },
      { path: 'articles/:slug', element: <ArticlePage /> },
      { path: 'auth', element: <AuthPage /> },
      {
        path: 'profile',
        element: (
          <RequireAuth>
            <ProfilePage />
          </RequireAuth>
        ),
      },
      // Token-based landing pages reached from email links.
      { path: 'subscribe/confirm', element: <SubscribeConfirmPage /> },
      { path: 'unsubscribe', element: <UnsubscribePage /> },
      { path: 'invite/accept', element: <InviteAcceptPage /> },
    ],
  },
  {
    path: ROUTES.write,
    errorElement: <RouteErrorBoundary />,
    element: (
      <RequireRole role={['writer', 'admin']}>
        <EditorPage />
      </RequireRole>
    ),
  },
  {
    path: 'write/:id',
    errorElement: <RouteErrorBoundary />,
    element: (
      <RequireRole role={['writer', 'admin']}>
        <EditorPage />
      </RequireRole>
    ),
  },
  {
    path: ROUTES.admin,
    errorElement: <RouteErrorBoundary />,
    element: (
      <RequireRole role="admin">
        <AdminLayout />
      </RequireRole>
    ),
    children: [
      { index: true, element: <AdminDashboardPage /> },
      { path: 'articles', element: <AdminArticlesPage /> },
      { path: 'writers', element: <AdminWritersPage /> },
      { path: 'subscribers', element: <AdminSubscribersPage /> },
      { path: 'settings', element: <AdminSettingsPage /> },
    ],
  },
  { path: '*', element: <NotFoundPage /> },
]);
